import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Package, MapPin, Clock, CheckCircle2, XCircle, LogOut, MessageCircle, ArrowRight } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { toast } from 'sonner';
import { Messaging } from '../components/Messaging';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  validateSearch: (search: Record<string, unknown>): { tab?: 'orders' | 'messages' } => {
    return {
      tab: search.tab as 'orders' | 'messages' | undefined
    };
  },
  component: ProfilePage,
});

interface Transaction {
  id: string;
  items: any[];
  totalAmount: number;
  status: string;
  fulfillmentStatus?: string;
  pickupTime?: string;
  pickupTimes?: Record<string, string>;
  createdAt: any;
}

interface UserProfile {
  email: string;
  displayName?: string;
}

function ProfilePage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [vendorRequest, setVendorRequest] = useState<any>(null);
  const [vendorDetails, setVendorDetails] = useState<Record<string, { name: string; location: string }>>({});
  const [isRequesting, setIsRequesting] = useState(false);
  const activeTab = search.tab || 'orders';

  const setActiveTab = (tab: 'orders' | 'messages') => {
    navigate({ to: '/profile', search: { tab }, replace: true });
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate({ to: '/' });
        return;
      }

      try {
        // Fetch profile
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfile({ ...data, uid: user.uid });
        }

        // Fetch vendor requests
        const reqQuery = query(collection(db, 'vendor_requests'), where('userId', '==', user.uid));
        const reqSnapshot = await getDocs(reqQuery);
        if (!reqSnapshot.empty) {
          setVendorRequest(reqSnapshot.docs[0].data());
        }

        // Fetch transactions
        const q = query(collection(db, 'transactions'), where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        const txs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Transaction[];
        
        // Sort by date desc
        txs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setTransactions(txs);

        // Fetch vendor details
        const uniqueVendorIds = Array.from(new Set(txs.flatMap(tx => Object.keys(tx.pickupTimes || {}))));
        const details: Record<string, { name: string; location: string }> = {};
        for (const vId of uniqueVendorIds) {
          const storeDoc = await getDoc(doc(db, 'stores', vId));
          if (storeDoc.exists()) {
            const data = storeDoc.data();
            details[vId] = {
              name: data.storeName || 'Vendor',
              location: data.pickupLocation || 'No location set'
            };
          }
        }
        setVendorDetails(details);
      } catch (err) {
        console.error('Error fetching profile data:', err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center h-[60vh] bg-neutral-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
      <Helmet>
        <title>My Account - Cardston Market</title>
        <meta name="description" content="Manage your orders, messages, and account settings on Cardston Market." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white">My Account</h1>
        <button 
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>

      <div className="flex border-b border-neutral-200 dark:border-neutral-800 mb-8">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'orders'
              ? 'border-neutral-900 dark:border-white text-neutral-900 dark:text-white'
              : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
          }`}
        >
          Orders & Address
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'messages'
              ? 'border-neutral-900 dark:border-white text-neutral-900 dark:text-white'
              : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
          }`}
        >
          Messages
        </button>
      </div>

      {activeTab === 'orders' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Info Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <h2 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Profile Details</h2>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-neutral-500 dark:text-neutral-400 block text-xs uppercase tracking-wider mb-1">Email</span>
                <span className="text-neutral-900 dark:text-white">{profile?.email || auth.currentUser?.email}</span>
              </div>
              {profile?.role === 'user' && (
                <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">Want to sell your own products?</p>
                  
                  {vendorRequest ? (
                    <div className={`p-4 rounded-xl border text-xs font-medium space-y-2 ${
                      vendorRequest.status === 'pending' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-900/50 text-amber-700 dark:text-amber-400' :
                      vendorRequest.status === 'rejected' ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900/50 text-red-700 dark:text-red-400' :
                      'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="uppercase tracking-wider">Status</span>
                        <span className="font-bold">{vendorRequest.status.toUpperCase()}</span>
                      </div>
                      {vendorRequest.status === 'pending' && <p className="font-normal opacity-80">Our team is reviewing your request.</p>}
                      {vendorRequest.status === 'rejected' && <p className="font-normal opacity-80">Unfortunately, your request was not accepted at this time.</p>}
                      {vendorRequest.status === 'approved' && <p className="font-normal opacity-80">Your request was approved! Refresh to see your vendor dashboard.</p>}
                    </div>
                  ) : (
                    <button
                      disabled={isRequesting}
                      onClick={async () => {
                        if (!auth.currentUser) return;
                        setIsRequesting(true);
                        try {
                          await addDoc(collection(db, 'vendor_requests'), {
                            userId: auth.currentUser.uid,
                            email: auth.currentUser.email,
                            storeName: profile?.displayName || 'My Store',
                            storeDescription: 'New vendor store application.',
                            status: 'pending',
                            createdAt: serverTimestamp()
                          });
                          toast.success('Vendor request submitted! Admin will review it soon.');
                          window.location.reload();
                        } catch (err) {
                          console.error(err);
                          toast.error('Failed to submit request.');
                        } finally {
                          setIsRequesting(false);
                        }
                      }}
                      className="w-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 py-2 rounded-xl text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {isRequesting ? 'Submitting...' : 'Apply to be a Vendor'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order History */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <h2 className="text-xl font-medium text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
              <Package className="w-5 h-5 text-neutral-400 dark:text-neutral-500" /> Order History
            </h2>
            
            {transactions.length === 0 ? (
              <div className="text-center py-24 bg-neutral-50 dark:bg-neutral-800/10 rounded-xl border border-neutral-100 dark:border-neutral-800">
                <Package className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4 stroke-[1.5]" />
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">No orders yet</h3>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 font-light max-w-xs mx-auto">When you buy things from our vendors, you'll be able to track your orders here.</p>
                <button onClick={() => navigate({ to: '/' })} className="mt-6 inline-flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors shadow-sm">Start Shopping</button>
              </div>
            ) : (
              <div className="space-y-6">
                {transactions.map(tx => (
                  <div key={tx.id} className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-sm">
                    <div className="bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">Order Placed</p>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">Total</p>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">${(tx.totalAmount / 100).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-0.5">Order #</p>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white font-mono">{tx.id?.slice(0, 8)}...</p>
                      </div>
                      {tx.pickupTime && (
                        <div className="flex items-center gap-1.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 px-3 py-1.5 rounded-lg">
                          <Clock className="w-3.5 h-3.5 text-neutral-400" />
                          <div>
                            <p className="text-[10px] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Pickup</p>
                            <p className="text-xs font-medium text-neutral-900 dark:text-white">{tx.pickupTime}</p>
                          </div>
                        </div>
                      )}

                      {tx.pickupTimes && Object.keys(tx.pickupTimes).length > 0 && (
                        <div className="w-full mt-4 space-y-3">
                          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 flex items-center gap-1.5">
                            <Clock className="w-4 h-4" /> Vendor-Specific Pickups
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {Object.entries(tx.pickupTimes).map(([vId, timeStr]) => {
                              const detail = vendorDetails[vId];
                              return (
                                <div key={vId} className="p-3 bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-xl space-y-2">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="text-xs font-bold text-neutral-900 dark:text-white">{detail?.name || 'Vendor'}</p>
                                      <p className="text-[10px] text-neutral-500">{timeStr}</p>
                                    </div>
                                    <MapPin className="w-4 h-4 text-neutral-300" />
                                  </div>
                                  {detail?.location && (
                                    <div className="pt-2 border-t border-neutral-50 dark:border-neutral-800/50">
                                      <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mb-1">Pickup Location:</p>
                                      <div className="flex items-center justify-between gap-2">
                                        <p className="text-[10px] text-neutral-700 dark:text-neutral-300 truncate flex-1">{detail.location}</p>
                                        <a 
                                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detail.location)}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[10px] font-bold text-neutral-900 dark:text-white flex items-center gap-0.5 hover:underline decoration-neutral-400"
                                        >
                                          Maps <ArrowRight className="w-2.5 h-2.5" />
                                        </a>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        {tx.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/50">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </span>
                        ) : tx.status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 border border-red-200/50 dark:border-red-900/50">
                            <XCircle className="w-3 h-3" /> Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400 border border-amber-200/50 dark:border-amber-900/50">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                        
                        {tx.status === 'completed' && (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            tx.fulfillmentStatus === 'preparing' ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400 border border-amber-200/50 dark:border-amber-900/50' :
                            tx.fulfillmentStatus === 'ready_for_pickup' ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 border border-blue-200/50 dark:border-blue-900/50' :
                            tx.fulfillmentStatus === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/50' :
                            'bg-neutral-100 dark:bg-neutral-800/50 text-neutral-800 dark:text-neutral-400 border border-neutral-200/50 dark:border-neutral-700/50'
                          }`}>
                            <Package className="w-3 h-3" /> {tx.fulfillmentStatus ? tx.fulfillmentStatus.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Pending'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Visual Progress Tracker string interpolation */}
                    {tx.status === 'completed' && (
                      <div className="px-4 py-4 md:px-8 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50">
                        <div className="relative">
                          <div className="absolute top-1/2 left-0 w-full h-1 flex -translate-y-1/2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-800">
                             <div className={`h-full transition-all duration-500 rounded-full ${tx.fulfillmentStatus === 'completed' ? 'bg-emerald-500 w-full' : tx.fulfillmentStatus === 'ready_for_pickup' ? 'bg-blue-500 w-2/3' : tx.fulfillmentStatus === 'preparing' ? 'bg-amber-500 w-1/3' : 'bg-neutral-400 w-0'}`}></div>
                          </div>
                          
                          <div className="relative flex justify-between">
                            <div className="flex flex-col items-center">
                               <div className={`w-4 h-4 rounded-full border-2 bg-white dark:bg-neutral-900 z-10 
                                 ${!tx.fulfillmentStatus || tx.fulfillmentStatus === 'pending' || tx.fulfillmentStatus === 'preparing' || tx.fulfillmentStatus === 'ready_for_pickup' || tx.fulfillmentStatus === 'completed' ? 'border-neutral-900 dark:border-white' : 'border-neutral-300 dark:border-neutral-700'}`}></div>
                               <span className="text-[10px] sm:text-xs font-medium text-neutral-600 dark:text-neutral-400 mt-2 text-center absolute top-5 -ml-4 w-12 sm:w-20">Pending</span>
                            </div>
                            <div className="flex flex-col items-center">
                               <div className={`w-4 h-4 rounded-full border-2 bg-white dark:bg-neutral-900 z-10 
                                 ${tx.fulfillmentStatus === 'preparing' || tx.fulfillmentStatus === 'ready_for_pickup' || tx.fulfillmentStatus === 'completed' ? 'border-amber-500 bg-amber-500' : 'border-neutral-300 dark:border-neutral-700'}`}></div>
                               <span className="text-[10px] sm:text-xs font-medium text-neutral-600 dark:text-neutral-400 mt-2 text-center absolute top-5 -ml-4 w-12 sm:w-20">Preparing</span>
                            </div>
                            <div className="flex flex-col items-center">
                               <div className={`w-4 h-4 rounded-full border-2 bg-white dark:bg-neutral-900 z-10 
                                 ${tx.fulfillmentStatus === 'ready_for_pickup' || tx.fulfillmentStatus === 'completed' ? 'border-blue-500 bg-blue-500' : 'border-neutral-300 dark:border-neutral-700'}`}></div>
                               <span className="text-[10px] sm:text-xs font-medium text-neutral-600 dark:text-neutral-400 mt-2 text-center absolute top-5 -ml-8 w-20">Ready for Pickup</span>
                            </div>
                            <div className="flex flex-col items-center">
                               <div className={`w-4 h-4 rounded-full border-2 bg-white dark:bg-neutral-900 z-10 
                                 ${tx.fulfillmentStatus === 'completed' ? 'border-emerald-500 bg-emerald-500' : 'border-neutral-300 dark:border-neutral-700'}`}></div>
                               <span className="text-[10px] sm:text-xs font-medium text-neutral-600 dark:text-neutral-400 mt-2 text-center absolute top-5 -mr-4 w-12 sm:w-20 sm:-mr-8">Completed</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="p-4 bg-white dark:bg-neutral-900">
                      <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                        {tx.items.map((item, idx) => (
                          <li key={idx} className="py-3 flex items-center gap-4 first:pt-0 last:pb-0">
                            <div className="w-12 h-12 rounded bg-neutral-100 dark:bg-neutral-800 overflow-hidden shrink-0">
                              <img src={item.imageUrl || 'https://picsum.photos/seed/product/100/100'} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">{item.name}</p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400">Qty: {item.quantity}</p>
                            </div>
                            <div className="text-sm font-medium text-neutral-900 dark:text-white">
                              ${((item.price * item.quantity) / 100).toFixed(2)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <Messaging role="customer" />
        </div>
      )}
    </div>
  );
}
